import React, { useMemo, useState } from 'react';
import { Spinner } from 'react-bootstrap';
import { useSelector } from 'react-redux';

import Snackbar from 'components/DesignSystem/Snackbar';
import Graph from 'components/Flux/Automatic/Daily/Graph';
import FullControlPanelSheet from 'components/Flux/Automatic/Mobile/FullControlPanelSheet';
import QuerySummary from 'components/Flux/Automatic/Mobile/QuerySummary';
import SavePreferencesModal from 'components/Flux/Automatic/ControlPanel/SavedPreferences/PreferenceSavingModal';
import useDailyData from 'utils/hooks/flux/automatic/useDailyData';
import useFluxAutomaticZipDownload from 'utils/hooks/flux/automatic/useFluxAutomaticZipDownload';
import usePastSimulationNames from 'utils/hooks/flux/automatic/usePastSimulationNames';
import useAcomphMLT from 'utils/hooks/flux/automatic/useAcomphMLT';
import { DailyData, buildSubdivisionParam } from 'helper/flux/automatic';
import {
  selectFluxAutomaticControlPanelSpatialSubdivision,
  selectFluxAutomaticControlPanelAutomaticStudyScenarios,
} from 'redux/reducers/fluxAutomaticControlPanelReducer';
import { cssVar } from 'constants/theme';

import FullscreenChart from './FullscreenChart';

import './index.scss';

export default function DailyMobile() {
  const [sheetOpen, setSheetOpen] = useState(false);
  const [fullscreenOpen, setFullscreenOpen] = useState(false);

  const { pastSimulationNames } = usePastSimulationNames();
  const models = useSelector(selectFluxAutomaticControlPanelAutomaticStudyScenarios);
  const subdivision = useSelector(selectFluxAutomaticControlPanelSpatialSubdivision);
  const { data, isFetching } = useDailyData();
  const { acomph, mlt } = useAcomphMLT();

  const dataIsEmpty = Object.keys(data).length === 0;
  const spatialSubdivisionParam = useMemo(() => buildSubdivisionParam(subdivision), [subdivision]);
  const { DownloadZipButton, DownloadConfirmationDialog } = useFluxAutomaticZipDownload({
    models,
    type: 'daily',
    pastSimulationNames,
    spatialSubdivisionParam,
  });

  const extraChips = useMemo(() => {
    const result: string[] = [];
    if (acomph) result.push('ACOMPH');
    if (mlt) result.push('MLT');
    return result;
  }, [acomph, mlt]);

  return (
    <div className="flux-automatic-daily-mobile">
      <DownloadConfirmationDialog />
      <SavePreferencesModal />

      <QuerySummary
        onOpenPanel={() => setSheetOpen(true)}
        extraChips={extraChips}
        action={models.length ? <DownloadZipButton /> : undefined}
      />

      {isFetching && <div className="text-center py-4"><Spinner /></div>}

      {!isFetching && dataIsEmpty && (
        <Snackbar>
          Nenhum dado para os filtros selecionados.
        </Snackbar>
      )}

      {!isFetching && !dataIsEmpty && (
        <div className="flux-automatic-daily-mobile-chart-card">
          <div className="flux-automatic-daily-mobile-chart-card-header">
            <span className="flux-automatic-daily-mobile-chart-card-title">Vazão diária</span>
            <button
              type="button"
              aria-label="Tela cheia"
              className="flux-automatic-daily-mobile-chart-card-action"
              onClick={() => setFullscreenOpen(true)}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={cssVar('text-strong')} strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round">
                <path d="M8 3H5a2 2 0 0 0-2 2v3M16 3h3a2 2 0 0 1 2 2v3M8 21H5a2 2 0 0 1-2-2v-3M16 21h3a2 2 0 0 0 2-2v-3" />
              </svg>
            </button>
          </div>
          <Graph data={data as DailyData} height="65vh" />
        </div>
      )}

      <FullControlPanelSheet
        open={sheetOpen}
        dismiss={() => setSheetOpen(false)}
        tab="daily"
      />
      <FullscreenChart
        open={fullscreenOpen}
        dismiss={() => setFullscreenOpen(false)}
        data={data as DailyData}
      />
    </div>
  );
}
